import { type RefObject, useEffect, useRef, useState } from 'react';
import type { Action, FighterInit } from '@cs-shot/battle-sim';
import type { Scene } from '../render/types';
import { createBattleEngine } from '../render/battleEngine';
import { checkBlackBackdrop } from './blackBackdropDiag';
import type { BattleAssets } from './battleAssets';
import type { RoundUpdate } from './useRushMidMatch';
import type { useHaptic } from '../../../hooks/useHaptic';
import { DESKTOP_QUERY } from '../../../contexts/ViewportContext';

export const WORLD_W = 1024;
export const WORLD_H = 1536;
export const ACTION_BAR_RESERVE = 0;

export type BattleEngine = ReturnType<typeof createBattleEngine>;

export interface BattleCanvasRefs {
  updateRef: RefObject<RoundUpdate | null>;
  initsRef: RefObject<FighterInit[]>;
  youIdRef: RefObject<string | null>;
  targetRef: RefObject<string | null>;
  chosenActionRef: RefObject<Action | null>;
  hapticRef: RefObject<ReturnType<typeof useHaptic>>;
  missLabelsRef: RefObject<{ miss: string; evade: string; blind: string }>;
}

export function useBattleCanvas(assets: BattleAssets | null, replay: boolean, refs: BattleCanvasRefs) {
  const { updateRef, initsRef, youIdRef, targetRef, chosenActionRef, hapticRef, missLabelsRef } = refs;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<BattleEngine | null>(null);

  const [field, setField] = useState<{ x: number; width: number }>({ x: 0, width: 0 });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const measure = () => {
      const cw = canvas.clientWidth || WORLD_W;
      const ch = canvas.clientHeight || WORLD_H;
      const availH = Math.max(1, ch - ACTION_BAR_RESERVE);
      const scale = Math.min(cw / WORLD_W, availH / WORLD_H);
      const width = WORLD_W * scale;
      setField({ x: (cw - width) / 2, width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!assets) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let disposed = false;

    const sc: Scene = {
      ctx, W: WORLD_W, H: WORLD_H,
      bg: assets.bg, explosionImg: assets.explosionImg, explosionFrames: assets.explosionFrames,
      teamIcons: assets.teamIcons,
      bodies: assets.bodies, runFrames: assets.runFrames, weapons: assets.weapons, weaponSkins: assets.weaponSkins, weaponIcons: assets.weaponIcons, headshotIcon: assets.headshotIcon, nade: assets.nade, smokeNade: assets.smokeNade, flashNade: assets.flashNade, knife: assets.knife, slash: assets.slash, smokeCloud: assets.smokeCloud, casing: assets.casing,
      utilIcons: assets.utilIcons,
      fighters: [], byId: {},
      bullets: [], casings: [], muzzlePuffs: [], flashes: [], impacts: [], grenades: [], blasts: [], flashbangs: [], slashes: [], damages: [], killFeed: [],
    };

    const engine = createBattleEngine(sc, {
      replay,
      getYouId: () => youIdRef.current,
      getTarget: () => targetRef.current,
      getChosenAction: () => chosenActionRef.current,
      onYouHit: () => hapticRef.current.impact('rigid'),
      onYouDeath: () => { hapticRef.current.notification('error'); hapticRef.current.impact('heavy'); },
      getMissLabels: () => missLabelsRef.current,
    });
    engineRef.current = engine;

    let safeTop = readSafeTop();
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round((canvas.clientWidth || WORLD_W) * dpr);
      canvas.height = Math.round((canvas.clientHeight || WORLD_H) * dpr);
      safeTop = readSafeTop();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const loop = () => {
      if (disposed) return;
      const now = performance.now() / 1000;

      const u = updateRef.current;
      if (u) engine.setUpdate(u, initsRef.current, now);

      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const cw = canvas.clientWidth || WORLD_W;
      const ch = canvas.clientHeight || WORLD_H;
      engine.step(now, cw, ch, dpr, safeTop, replay ? 0 : ACTION_BAR_RESERVE);
      checkBlackBackdrop(sc, cw, ch, dpr);

      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { disposed = true; cancelAnimationFrame(raf); ro.disconnect(); engineRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, replay]);

  return { canvasRef, engineRef, field };
}

// WHY: на десктопе CSS-переменные safe-area могут нести протухшие ненулевые значения — guard перед чтением
function readSafeTop(): number {
  if (typeof window === 'undefined') return 0;
  if (window.matchMedia(DESKTOP_QUERY).matches) return 0;
  const s = getComputedStyle(document.documentElement);
  const px = (v: string) => parseFloat(s.getPropertyValue(v)) || 0;
  return px('--tg-viewport-safe-area-inset-top') + px('--tg-viewport-content-safe-area-inset-top');
}
